import { Params } from '@angular/router';
import { ClientSettingsService } from '../services/ClientSettingsService';
import { AppStateService } from '../services/AppStateService';
import { DisplayStateService } from './../services/DisplayStateService';
import { isNumeric } from 'rxjs/util/isNumeric';

import * as WI from '../interfaces/WidgetInterfaces';

export class UtilityFunctions {

    static Log(logLevel: number, message: string, c: ClientSettingsService, a: AppStateService, params?: Params) {
        if (logLevel > 5 || logLevel < 1) {
            logLevel = 5;
        }
        let logLevelText = 'TRACE';
        switch (logLevel) {
            case 1:  logLevelText = 'ERROR'; break;
            case 2:  logLevelText = 'WARNING'; break;
            case 3:  logLevelText = 'MAJOR'; break;
            case 4:  logLevelText = 'MINOR'; break;
            case 5:  logLevelText = 'TRACE'; break;

        }
        
        if (c.ConsoleLogLevel >= logLevel) {
            console.log(`${logLevelText} => ${message}: ${UtilityFunctions.SortedParamsString(params)}`);
        }

        if (c.AppLogLevel >= logLevel && a !== undefined) {
            a.appendLog(`${message}: ${UtilityFunctions.SortedParamsString(params)}`, logLevel);
        }
    }

    static checkApiVersion(minimumVersionComponents: Number[], displayStateService: DisplayStateService): ApiVersionCheckResults {

        const results: ApiVersionCheckResults = {
            minimumRequiredVersion: '',
            ApiVersion: '',
            pass: false,
            error: false,
            message: '',
            errorMessage: ''
        }

        results.minimumRequiredVersion = minimumVersionComponents.map(x => x.toString()).join('.');
        
        let error = undefined;

        if (!displayStateService) {
            results.errorMessage = 'checkApiVersion: displayStateService undefined';
            results.error = true;
            return results;
        }
        if (!displayStateService.appState.apiVersion) {
            results.errorMessage = 'checkApiVersion: displayStateService.appState.apiVersion undefined';
            results.error = true;
            return results;
        }

        const versionComponents = displayStateService.appState.apiVersion.split('.');
        if (versionComponents.length < 4) {
            results.error = true;
            results.errorMessage = `Value ${displayStateService.appState.apiVersion} cannot be parsed into 4 components.`
            return results;
        } else {
          for (let i = 0; i < 4; i++) {
            if (!isNumeric(versionComponents[i])) {
                results.error = true;
                results.errorMessage = `Invalid version component value "${versionComponents[i]}" at position ${i} for version ${displayStateService.appState.apiVersion}.`;
                return results;
            } else {
              let currentValue = Number(versionComponents[i]);
              if (currentValue > minimumVersionComponents[i]) {
                
                break;
              } else if (currentValue < minimumVersionComponents[i]) {                
                results.message = `api version ${results.ApiVersion} DOES NOT meet minimum version requirement ${results.minimumRequiredVersion}`;
                return results;                
              }
            }
          }
        }
        results.pass = true;
        results.message = `api version ${results.ApiVersion} meets minimum version requirement ${results.minimumRequiredVersion}`;
        return results;
    }

    static InitializeDataConsumerConfigForEdit(dcc: WI.DataConsumerConfiguration) {

        if (!dcc) {
            return;
        }

        if (dcc.sortColumns === undefined) { dcc.sortColumns = []; }
        if (dcc.overrideCacheRules === undefined) {
            dcc.overrideCacheRules = { period: '' };
        }
        if (dcc.dataGrouping === undefined) {
            dcc.dataGrouping = {
                serverSide: false,
                groupBy: [],
                aggregations: []
            };
        }

        if (dcc.pivot === undefined) {
            dcc.pivot = {
                name: '',
                operation: '',
                rename: ''
            };
        }

        if (dcc.fixedParameters === undefined) { dcc.fixedParameters = []; }
        if (dcc.contextParameters === undefined) { dcc.contextParameters = []; }
        if (dcc.filterConfiguration === undefined) { dcc.filterConfiguration = []; }

    }

    static SortedParamsString(params: Params): string {

        if (params !== undefined && Object.keys(params).length > 0) {
            return Object.keys(params).sort().map(k => k + '=' + params[k]).join('|');
        } else {
            return '';
        }
        
    }

    static MatchWidgetConfigurations(context: Params, masterWidgetConfigurationList: WI.WidgetConfiguration[],
        c: ClientSettingsService, a: AppStateService): WI.WidgetConfiguration[] {

        const ret: WI.WidgetConfiguration[] = [];
        masterWidgetConfigurationList.map(masterWC => {
            if (UtilityFunctions.MatchWidgetConfiguration(context, masterWC, c, a)) {
                ret.push(masterWC);
            }
        });

        return ret;
    }

    static CreateContextKey(matchingWidgets: WI.WidgetConfiguration[], currentContext: Params): string {
        const keyElements: Params = [];
        const contextNames = Object.keys(currentContext).map(x => x.toLowerCase());
        matchingWidgets.forEach(matchingWC => {
            matchingWC.contextMatchingRules.forEach(cmr => {
                if (cmr.contextName !== '*') {
                    if (!cmr.optional || contextNames.indexOf(cmr.contextName.toLowerCase()) > -1) {
                        if (keyElements[cmr.contextName] === undefined) {
                            keyElements[cmr.contextName] = cmr.valueMatch;
                        } else if (!keyElements[cmr.contextName]) {
                            keyElements[cmr.contextName] = cmr.valueMatch;
                        }
                    }
                }
            });
        });

        return Object.keys(keyElements).map(k => { return keyElements[k] ? `${k}=${keyElements[k]}` : `${k}`; }).join('|');
    }

    static ListContains(list: string[], value: string) {
        return (list.findIndex(x => x === value) > -1) || (list.findIndex(x => x === '*') > -1);
    }

    static MatchWidgetConfiguration(context: Params, masterWC: WI.WidgetConfiguration, c: ClientSettingsService, a: AppStateService) {


        const remainingKeys = Object.keys(context).map(x => x.toLowerCase()).slice();

        let matches = true;

        let whyNot = '';

        if (!masterWC.contextMatchingRules || masterWC.contextMatchingRules.length === 0) {
            matches = false;
            whyNot = 'No context matching rules defined for widget';
        } else {

            for (const rule of masterWC.contextMatchingRules) {

                // Check if context key exists
                const exists = Object.keys(context).find(e => e.toLowerCase() === rule.contextName.toLowerCase());

                // Knock off the key from remaining - at the end we see if any are remaining that don't have a rule
                // as a way of failing the match
                if (exists && remainingKeys.indexOf(rule.contextName.toLowerCase()) > -1) {
                    remainingKeys.splice(remainingKeys.indexOf(rule.contextName.toLowerCase()), 1);
                }

                if (!exists && !rule.optional) {
                    matches = false;
                    whyNot = `${rule.contextName} isn't optional and doesn't exist`;
                    break;
                } else if (
                    rule.valueMatch && context[exists] &&
                    context[exists].toLowerCase() !== rule.valueMatch.toLowerCase()) {

                    matches = false;
                    whyNot = `${rule.contextName}.${rule.valueMatch}" didn't match "${context[exists]}"`;
                    break;
                }
            }

            if (matches) {
                if (remainingKeys.length > 0) {
                    matches = false;
                    whyNot = 'extra keys in context not accounted for: ' + JSON.stringify(remainingKeys);
                }
            }
        }

        // if (matches) {
        //     UtilityFunctions.Log(3, `Match for ${masterWC.name}`, c, a);
        // } else {
        //     UtilityFunctions.Log(3, `No match for ${masterWC.name} : ${whyNot}`, c, a);
        // }
        // if (!matches) { console.log(whyNot); }

        return matches;
    }
}

export class Dictionary<K, V> {
    private _keys: K[];
    private _values: V[];

    constructor() {
        this._keys = [];
        this._values = [];
    }

    upsert(k: K, v: V) {
        const idx = this._keys.indexOf(k);
        if (idx === -1) {
            this._keys.push(k);
            this._values.push(v);
        } else {
            this._values[idx] = v;
        }
    }

    keys(): K[] {
        return this._keys.slice();
    }

    values(): V[] {
        return this._values.slice();
    }

    remove(k: K) {
        const idx = this._keys.indexOf(k);
        if (idx > -1) {
            this._keys.splice(idx, 1);
            this._values.splice(idx, 1);
        }
    }

    get(k: K): V {
        const idx = this._keys.indexOf(k);
        if (idx > -1) {
            return this._values[idx];
        } else {
            return undefined;
        }
    }

    containsKey(k: K) {
        return (this._keys.indexOf(k) > -1);
    }

}

export class ApiVersionCheckResults {
    minimumRequiredVersion: string;
    ApiVersion: string;
    pass: boolean;
    error: boolean;
    message: string;
    errorMessage: string;    
}

export class WindowUtils {

    static STORAGE_TYPE_LOCAL = 'local';
    static STORAGE_TYPE_SESSION = 'session';

    static getNativeWindowObject(): Window {
        return window;
    }
}
